import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Group, PasswordInput, Text, TextInput, Title } from "@mantine/core";
import { forgotPassword, verifyResetCode, completePasswordReset } from "../../../services/auth/auth.service";

type Props = {
  initialEmail?: string;
  codeSent?: boolean;
  migrationRequired?: boolean;
  onLogin: (email: string) => void;
};

/** Recovery grants and passwords live only in this mounted form. */
export default function DatabaseResetPasswordPage({ initialEmail = "", codeSent = false, migrationRequired = false, onLogin }: Props) {
  const [email, setEmail] = useState(initialEmail);
  const [step, setStep] = useState<"email" | "code" | "password" | "done">(codeSent && initialEmail ? "code" : "email");
  const [code, setCode] = useState("");
  const [grant, setGrant] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [cooldown, setCooldown] = useState(codeSent ? 60 : 0);
  const [notice, setNotice] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => { heading.current?.focus(); }, [step]);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  useEffect(() => {
    if (step !== "password") return;
    const timer = window.setTimeout(() => {
      setGrant(""); setPassword(""); setConfirmation(""); setCode(""); setStep("email");
      setError("Your verification has expired. Request a new code to continue.");
    }, 5 * 60 * 1000);
    return () => window.clearTimeout(timer);
  }, [step]);

  const run = async (operation: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(""); setNotice("");
    try { await operation(); }
    catch (failure) {
      setError(failure?.message || "Unable to continue. Please try again.");
      if (step === "password" && failure?.code === "INVALID_CODE") {
        setGrant(""); setPassword(""); setConfirmation(""); setCode(""); setStep("email");
        setError("Your verification has expired or is no longer valid. Request a new code.");
      }
    } finally { inFlight.current = false; setBusy(false); }
  };
  const sendCode = () => run(async () => {
    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) throw new Error("Enter a valid email address.");
    const response = await forgotPassword(normalized);
    setEmail(normalized); setCode(""); setGrant(""); setStep("code");
    setCooldown(response?.cooldownSeconds || 60);
    setNotice("If your account is eligible, a code has been sent. Check your inbox and spam folder.");
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (step === "email") { void sendCode(); return; }
    void run(async () => {
      if (step === "code") {
        if (!/^\d{6}$/.test(code.trim())) throw new Error("Enter the six-digit code from your email.");
        const resetGrant = await verifyResetCode(email, code.trim());
        setGrant(resetGrant); setCode(""); setStep("password");
      } else if (step === "password") {
        if (Array.from(password).length < 15 || password.length > 128) throw new Error("Use a password of 15–128 characters.");
        if (password !== confirmation) throw new Error("Passwords did not match.");
        await completePasswordReset(email, grant, password);
        setGrant(""); setPassword(""); setConfirmation(""); setStep("done");
      }
    });
  };
  const restart = () => {
    setGrant(""); setCode(""); setPassword(""); setConfirmation("");
    setStep("email"); setError(""); setNotice("");
  };
  return (
    <div className="auth-container-box">
      <Box className="auth-single-form" sx={{ width: "100%", maxWidth: 480 }} mx="auto">
        <Title order={1} ref={heading} tabIndex={-1}>
          {step === "done" ? "Your password is ready" : step === "password" ? "Choose a new password" : step === "code" ? "Check your email" : "Reset your password"}
        </Title>
        {step !== "done" && <Text mt="sm" size="sm" color="dimmed">
          Step {step === "email" ? 1 : step === "code" ? 2 : 3} of 3 · Email → Verification code → New password
        </Text>}
        {migrationRequired && step !== "done" && <Text mt="md">
          We have updated account security. You need to reset your password to continue. Verify your email using a six-digit code, then choose a new password. Your courses and account details will stay with your account.
        </Text>}
        {step === "code" && <Text mt="md">
          Enter the six-digit code for {email}. Codes expire after 10 minutes. Only the most recent code works.
        </Text>}
        {step === "password" && <Text mt="md">Email verified: {email}. Choose a unique password of 15–128 characters. Complete this step within five minutes.</Text>}
        {notice && <Text role="status" mt="md">{notice}</Text>}
        {error && <Text role="alert" color="red" mt="md">{error}</Text>}
        {step === "done" ? <>
          <Text mt="md" role="status">Your account is ready. Sign in with your email and new password.</Text>
          <Button mt="lg" fullWidth onClick={() => onLogin(email)}>Go to login</Button>
        </> : <form onSubmit={submit} aria-busy={busy}>
          {step === "email" && <TextInput mt="md" required type="email" autoComplete="email" label="Account email"
            value={email} disabled={busy} onChange={event => setEmail(event.currentTarget.value)} />}
          {step === "code" && <TextInput mt="md" required autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}"
            maxLength={6} label="Email verification code" value={code} disabled={busy} onChange={event => setCode(event.currentTarget.value)} />}
          {step === "password" && <>
            <PasswordInput mt="md" required autoComplete="new-password" maxLength={128} label="New password"
              value={password} disabled={busy} onChange={event => setPassword(event.currentTarget.value)} />
            <PasswordInput mt="md" required autoComplete="new-password" maxLength={128} label="Confirm new password"
              value={confirmation} disabled={busy} onChange={event => setConfirmation(event.currentTarget.value)} />
          </>}
          <Button type="submit" mt="lg" fullWidth loading={busy} disabled={step === "email" && cooldown > 0}>
            {step === "email" ? (cooldown ? `Request a new code in ${cooldown}s` : "Send verification code") : step === "code" ? "Verify code" : "Save new password"}
          </Button>
          {step === "code" && <Group mt="sm">
            <Button type="button" variant="subtle" disabled={busy || cooldown > 0} onClick={() => void sendCode()}>
              {cooldown ? `Resend code in ${cooldown}s` : "Resend code"}
            </Button>
            <Button type="button" variant="subtle" disabled={busy} onClick={restart}>Change email</Button>
          </Group>}
          <Button type="button" mt="sm" variant="subtle" disabled={busy} onClick={() => onLogin(email)}>Back to login</Button>
        </form>}
      </Box>
    </div>
  );
}
